import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function BungeeJumping() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    { question: 'Is bungee jumping in Jinja safe?', answer: 'Absolutely. Operators adhere to New Zealand/Australian safety standards with ISO certification. Equipment is tested before every jump, and there have been zero accidents since operations began over a decade ago. Your safety is the top priority.' },
    { question: 'What is the water touch option?', answer: 'The "water touch" or "long jump" allows you to skim the Nile River with your hands or head during descent—a unique feature of Jinja\'s jump. You can choose this option or a standard jump that keeps you above the water.' },
    { question: 'What are the age and weight requirements?', answer: 'Minimum age is 13 years. Weight must be between 35kg and 260kg (77-573 lbs) for safety. You\'ll be precisely weighed during registration for proper cord selection.' },
    { question: 'Can I jump if I have health conditions?', answer: 'No. Individuals with heart conditions, high blood pressure, diabetes, neurological disorders, pregnancy, or recent injuries should not participate. Complete a health questionnaire honestly during registration.' },
    { question: 'What if I can\'t jump after getting harnessed?', answer: 'No pressure whatsoever. Fear is natural. While there\'s typically no refund, operators understand and won\'t force you. You can take your time or reschedule if possible.' },
    { question: 'Are tandem jumps available?', answer: 'Yes! You can jump with a friend or partner. Tandem jumps must be arranged in advance and both participants must meet weight requirements.' },
    { question: 'Do I get photos and video?', answer: 'Yes! Professional video and digital photos are included. You\'ll receive download links after your jump. Additional printed photos or USB copies may be available for purchase.' },
    { question: 'What happens in bad weather?', answer: 'Jumps are not conducted during heavy rain, thunderstorms, or strong winds for safety. Your operator will reschedule you or offer alternatives. Morning slots generally have better weather.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1528543606781-2f6e6857f318?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Bungee Jumping Over the Nile
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Take the ultimate leap – 44 meters above the legendary Nile with optional water touch
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(850+ jumps completed)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                Safety Certified
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-bolt" className="w-4 h-4 mr-2" />
                Pure Adrenaline
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-water" className="w-4 h-4 mr-2" />
                Water Touch Option
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Jump
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Leap Into the Ultimate Adrenaline Rush Over the Nile
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Standing on the edge of a 44-meter platform suspended above the churning white waters of the Nile, your heart pounding, adrenaline surging—this is the moment that separates dreamers from doers. Bungee jumping in Jinja isn't just about the leap; it's about conquering your deepest fears, experiencing pure freedom during 3.5 seconds of free-fall, and joining thousands who've taken the plunge at one of the world's most iconic jump sites. As you count down—3, 2, 1—and launch yourself into the void, the legendary Nile rushes up to meet you before the premium bungee cord catches, sending you rebounding skyward with an exhilarating rush that'll stay with you forever.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Welcome to the Source of the Nile Bungee Jump—Africa's highest commercial bungee jump and consistently rated among the world's most scenic. Located in Jinja, Uganda's adventure capital, this isn't your typical bungee experience. You're suspended above the point where the mighty Nile begins its 6,650-kilometer journey to the Mediterranean Sea, surrounded by lush tropical scenery and the thunderous roar of rapids echoing through the gorge. The combination of spectacular location, professional operation, and genuine adrenaline rush makes this jump truly legendary.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">World-Class Safety Standards</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your safety is paramount. Operated by reputable companies like Adrift and Nile High Camp, Jinja's bungee operations adhere to strict international standards—specifically New Zealand and Australian safety protocols, considered the gold standard in bungee jumping. The operation holds ISO certification and uses premium equipment that meets U.S. and Canadian safety requirements. Every piece of equipment undergoes rigorous testing before each use, with detailed maintenance records ensuring nothing exceeds its safe working lifespan.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The bungee cords are made from high-quality latex rubber that meets global standards, while the platform utilizes pendulum bungee technology for smoother, more comfortable jumps. You'll be secured with a full-body harness combined with an ankle connection—double redundancy for maximum safety. International consultants review safety procedures annually, recommending improvements and ensuring operations remain world-class. Since operations began over a decade ago, there have been zero accidents—a testament to the uncompromising safety culture.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">The Unique Water Touch Experience</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  What makes Jinja's jump truly special is the optional "water touch" or "long jump"—a thrilling variation where you can actually kiss the legendary Nile River during your descent. As you plummet 44 meters, you'll skim the water's surface with your hands or head (depending on your preference) before the cord snaps you back up. This immersive element adds an extra dimension to an already incredible experience, giving you a tangible connection to one of the world's most famous rivers. Not many people can say they've touched the Source of the Nile while bungee jumping!
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Jump With Us
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-water', title: 'Water Touch Option', description: 'Skim the legendary Nile River during your descent—unique to Jinja!' },
                  { icon: 'fa-shield-halved', title: 'ISO Certified Safety', description: 'New Zealand/Australian standards, ISO certified operation, zero accidents' },
                  { icon: 'fa-video', title: 'Video & Photos Included', description: 'Professional footage of your jump—digital downloads included!' },
                  { icon: 'fa-star', title: 'Africa\'s Highest Jump', description: '44 meters—Africa\'s highest commercial bungee at the Source of the Nile' },
                  { icon: 'fa-users', title: 'Tandem Jumps Available', description: 'Share the experience—jump with a friend or loved one!' },
                  { icon: 'fa-certificate', title: 'Official Certificate', description: 'Receive a commemorative jump certificate as proof of your bravery' },
                  { icon: 'fa-cog', title: 'Premium Equipment', description: 'High-quality latex cords, pendulum technology, full-body harnesses' },
                  { icon: 'fa-clock', title: 'Quick & Flexible', description: 'Multiple daily slots—perfect add-on to your Jinja adventures' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Jump Day Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences
                  and pickup location. We can adjust timing to fit your schedule.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '09:00', title: 'Arrival & Registration', desc: 'Check in at the jump site. Complete health questionnaire, liability waiver, and precise weighing for cord selection.' },
                  { time: '09:15', title: 'Comprehensive Safety Briefing', desc: 'Certified jump master explains the entire process, safety systems, body position, and what to expect during free-fall.' },
                  { time: '09:30', title: 'Equipment Fitting', desc: 'Get fitted with full-body harness and ankle connection. Choose standard or water touch jump. Equipment double-checked by two staff.' },
                  { time: '09:45', title: 'Platform Walk', desc: 'Walk to the 44-meter platform. Breathe. Take in the spectacular Nile views. This is it!' },
                  { time: '10:00', title: 'THE JUMP!', desc: '5...4...3...2...1...BUNGEE! Leap into 3.5 seconds of pure free-fall. (Optional water touch on descent.) Rebound skyward!' },
                  { time: '10:10', title: 'Recovery & Celebration', desc: 'Return to platform. Adrenaline rush! High-fives all around. Watch your video footage on screen.' },
                  { time: '10:25', title: 'Certificate & Media', desc: 'Receive official jump certificate. Get download link for your professional video and photos.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Essentials
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Comfortable athletic wear (avoid loose clothing)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Secure closed-toe shoes (no flip-flops/sandals)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sunscreen, hat & sunglasses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Swimwear if choosing water touch option</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-id-card" className="w-6 h-6" />
                    Documents & Extras
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Valid ID or passport (required)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Booking confirmation voucher</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Water or drinks (optional)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Cash for tips or souvenirs</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Common Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Jinja Thrills
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/whitewater-rafting" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800"
                      alt="Whitewater Rafting"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Whitewater Rafting</h3>
                    <p className="text-gray-700 mb-4">Conquer Grade 5 rapids on the legendary Nile.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/kayaking-adventures" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1484876065684-b683cf17d276?w=800"
                      alt="Kayaking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Nile Kayaking</h3>
                    <p className="text-gray-700 mb-4">Learn to paddle the Nile's rapids or cruise calmly.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 1-2 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Mornings preferred</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Height:</strong> 44m (144 feet)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 13 years</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-weight-hanging" className="w-6 h-6 text-safari-green" />
                    <span><strong>Weight:</strong> 35-260 kg</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Jump
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "One bungee jump (standard or water touch)",
                    "Comprehensive safety briefing",
                    "Full-body harness & ankle connection",
                    "Professional video footage (digital)",
                    "Digital photo downloads",
                    "Official jump certificate",
                    "Recovery assistance & support"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to/from jump site",
                    "Additional jumps (can be purchased)",
                    "Printed photos or USB copies",
                    "Food, snacks & beverages",
                    "Gratuities for jump crew",
                    "Travel insurance"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Weight:</strong> 35-260kg strictly enforced.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Exclusions:</strong> Heart issues, high BP, pregnancy, diabetes, neurological conditions.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Weather:</strong> No jumps in heavy rain/storms.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Bungee Jumping Over the Nile"
      />
    </div>
  )
}
